// MACHA — Unified workspace data layer
// =====================================================================
// Projects + Conversations CRUD with two backends:
//   • Authenticated users  → Supabase (projects + conversations tables,
//                             RLS-enforced server-side)
//   • Anonymous users      → localStorage (so the trial / signed-out
//                             experience still works)
//
// Messages are stored in localStorage keyed by conversation id for BOTH
// modes (server-side message sync is deferred per PRD Phase 5.5).
// =====================================================================

import { supabase } from "@/lib/supabase";

// -------- Tool registry (shared, single source) ----------------------
export const TOOLS = [
  { id: "reply_assistant",    label: "AI Reply Assistant", short: "Reply" },
  { id: "proposal_generator", label: "Proposal Generator", short: "Proposal" },
  { id: "client_analyzer",    label: "Client Analyzer",    short: "Analyzer" },
];
export const TOOL_LABEL = Object.fromEntries(TOOLS.map((t) => [t.id, t.label]));

export function defaultSettings(tool) {
  if (tool === "reply_assistant")    return { tone: "professional" };
  if (tool === "proposal_generator") return { platform: "upwork", style: "professional" };
  if (tool === "client_analyzer")    return {};
  return {};
}

/** Build the payload the existing /api/ai/generate endpoint expects. */
export function buildPayload(tool, settings, userInput, prevUserInput) {
  if (tool === "reply_assistant") {
    if (!prevUserInput) {
      return {
        client_message: userInput,
        user_quick_reply: "Draft a thoughtful reply to this message.",
        selected_tone: settings.tone || "professional",
      };
    }
    return {
      client_message: prevUserInput,
      user_quick_reply: userInput,
      selected_tone: settings.tone || "professional",
    };
  }
  if (tool === "proposal_generator") {
    return {
      job_description: userInput,
      platform: settings.platform || "upwork",
      proposal_style: settings.style || "professional",
    };
  }
  if (tool === "client_analyzer") {
    return { job_description: userInput };
  }
  return {};
}

// -------- localStorage helpers --------------------------------------
const LS_PROJECTS      = "replyai.projects.v2";
const LS_CONVERSATIONS = "replyai.conversations.v2";
const LS_MESSAGES      = (id) => `replyai.messages.v1.${id}`;
const LS_SETTINGS      = (id) => `replyai.convSettings.v1.${id}`;

function lsLoad(key, fallback = []) {
  try { return JSON.parse(localStorage.getItem(key) || JSON.stringify(fallback)); }
  catch { return fallback; }
}
function lsSave(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* ignore */ }
}
function uid(prefix = "id") {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}
function nowIso() { return new Date().toISOString(); }

// ===================================================================
//  MESSAGES (always localStorage for now — server sync is deferred)
// ===================================================================
export const messagesStore = {
  load(convId) { return lsLoad(LS_MESSAGES(convId), []); },
  save(convId, messages) { lsSave(LS_MESSAGES(convId), messages); },
  append(convId, message) {
    const m = [...messagesStore.load(convId), message];
    messagesStore.save(convId, m);
    return m;
  },
  remove(convId) {
    try { localStorage.removeItem(LS_MESSAGES(convId)); } catch { /* ignore */ }
  },
};

// Settings are part of the conversation row in Supabase but we cache a
// per-conversation client copy too so tool chips persist across refreshes
// for anonymous users.
export const conversationSettingsStore = {
  load(convId, tool) {
    return lsLoad(LS_SETTINGS(convId), defaultSettings(tool));
  },
  save(convId, settings) { lsSave(LS_SETTINGS(convId), settings); },
};

// ===================================================================
//  PROJECTS
// ===================================================================

// Default shape for a project's knowledge base. Six free-text sections.
export const EMPTY_KNOWLEDGE = {
  client_info: "",
  requirements: "",
  brand_voice: "",
  portfolio_links: "",
  notes: "",
  instructions: "",
};

export const KNOWLEDGE_SECTIONS = [
  { key: "client_info",     label: "Client information",   placeholder: "Company name, contact person, industry, history, preferences, key stakeholders…" },
  { key: "requirements",    label: "Project requirements", placeholder: "Scope, deliverables, deadlines, success criteria, constraints, dependencies…" },
  { key: "brand_voice",     label: "Brand voice & tone",   placeholder: "How they speak: warm vs formal, jargon-heavy vs plain, examples of their writing…" },
  { key: "portfolio_links", label: "Portfolio & links",    placeholder: "Past work, references, docs, design files, repos, brand assets…" },
  { key: "notes",           label: "Notes",                placeholder: "Anything else worth remembering — quirks, do's and don'ts, sensitive topics…" },
  { key: "instructions",    label: "Important instructions", placeholder: "Strict rules the AI must follow in every reply (e.g. \"always sign off with…\", \"never quote a price below…\")." },
];

/** Merge stored knowledge with the empty shape — robust against schema drift. */
export function normalizeKnowledge(k) {
  return { ...EMPTY_KNOWLEDGE, ...(k && typeof k === "object" ? k : {}) };
}

/** Returns a clean markdown block ready to send as `project_context`. Only
 *  non-empty sections are included. Returns "" when knowledge is fully empty.
 */
export function formatKnowledgeForPrompt(knowledge) {
  const k = normalizeKnowledge(knowledge);
  const parts = [];
  for (const s of KNOWLEDGE_SECTIONS) {
    const v = (k[s.key] || "").trim();
    if (v) parts.push(`## ${s.label}\n${v}`);
  }
  return parts.join("\n\n");
}

/** Count of non-empty knowledge sections — used for badges in the UI. */
export function knowledgeFilledCount(knowledge) {
  const k = normalizeKnowledge(knowledge);
  return KNOWLEDGE_SECTIONS.filter((s) => (k[s.key] || "").trim()).length;
}

export async function listProjects(user) {
  if (user) {
    const { data, error } = await supabase
      .from("projects")
      .select("id, name, knowledge, created_at, updated_at")
      .order("updated_at", { ascending: false });
    if (error) throw error;
    return (data || []).map((p) => ({ ...p, knowledge: normalizeKnowledge(p.knowledge) }));
  }
  return lsLoad(LS_PROJECTS)
    .map((p) => ({ ...p, knowledge: normalizeKnowledge(p.knowledge) }))
    .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
}

export async function createProject(user, name) {
  const clean = (name || "").trim() || "Untitled project";
  if (user) {
    const { data, error } = await supabase
      .from("projects")
      .insert({ user_id: user.id, name: clean, knowledge: {} })
      .select("id, name, knowledge, created_at, updated_at")
      .single();
    if (error) throw error;
    return { ...data, knowledge: normalizeKnowledge(data.knowledge) };
  }
  const proj = {
    id: uid("p"), name: clean, knowledge: { ...EMPTY_KNOWLEDGE },
    created_at: nowIso(), updated_at: nowIso(),
  };
  lsSave(LS_PROJECTS, [proj, ...lsLoad(LS_PROJECTS)]);
  return proj;
}

export async function renameProject(user, id, name) {
  const clean = (name || "").trim();
  if (!clean) throw new Error("Project name cannot be empty");
  if (user) {
    const { data, error } = await supabase
      .from("projects")
      .update({ name: clean, updated_at: nowIso() })
      .eq("id", id)
      .select("id, name, knowledge, created_at, updated_at")
      .single();
    if (error) throw error;
    return { ...data, knowledge: normalizeKnowledge(data.knowledge) };
  }
  const all = lsLoad(LS_PROJECTS);
  const idx = all.findIndex((p) => p.id === id);
  if (idx === -1) return null;
  all[idx] = { ...all[idx], name: clean, updated_at: nowIso() };
  lsSave(LS_PROJECTS, all);
  return { ...all[idx], knowledge: normalizeKnowledge(all[idx].knowledge) };
}

export async function updateProjectKnowledge(user, id, knowledge) {
  const clean = normalizeKnowledge(knowledge);
  if (user) {
    const { data, error } = await supabase
      .from("projects")
      .update({ knowledge: clean, updated_at: nowIso() })
      .eq("id", id)
      .select("id, name, knowledge, created_at, updated_at")
      .single();
    if (error) throw error;
    return { ...data, knowledge: normalizeKnowledge(data.knowledge) };
  }
  const all = lsLoad(LS_PROJECTS);
  const idx = all.findIndex((p) => p.id === id);
  if (idx === -1) return null;
  all[idx] = { ...all[idx], knowledge: clean, updated_at: nowIso() };
  lsSave(LS_PROJECTS, all);
  return { ...all[idx], knowledge: clean };
}

export async function deleteProject(user, id) {
  if (user) {
    // RLS + ON DELETE SET NULL handles conversations → keeps them, just
    // un-assigns them. They will appear in Recent Chats automatically.
    const { error } = await supabase.from("projects").delete().eq("id", id);
    if (error) throw error;
    return;
  }
  lsSave(LS_PROJECTS, lsLoad(LS_PROJECTS).filter((p) => p.id !== id));
  // Un-assign conversations locally
  const convs = lsLoad(LS_CONVERSATIONS);
  lsSave(
    LS_CONVERSATIONS,
    convs.map((c) => (c.project_id === id ? { ...c, project_id: null } : c))
  );
}

// ===================================================================
//  CONVERSATIONS
// ===================================================================
// Supabase row shape: { id, user_id, tool_type, title, project_id, created_at, updated_at }
// Client-side we also keep `settings` (per-conversation tool prefs) in
// localStorage keyed by id — see conversationSettingsStore above.

export async function listConversations(user) {
  if (user) {
    const { data, error } = await supabase
      .from("conversations")
      .select("id, tool_type, title, project_id, created_at, updated_at")
      .order("updated_at", { ascending: false });
    if (error) throw error;
    // Normalise tool field name to `tool` so the UI doesn't care
    return (data || []).map((c) => ({ ...c, tool: c.tool_type }));
  }
  return lsLoad(LS_CONVERSATIONS).sort(
    (a, b) => new Date(b.updated_at) - new Date(a.updated_at)
  );
}

export async function createConversation(user, { tool = "reply_assistant", title, project_id = null } = {}) {
  const cleanTitle =
    (title && title.trim()) || `${TOOL_LABEL[tool] || "New"} · New chat`;

  if (user) {
    const { data, error } = await supabase
      .from("conversations")
      .insert({
        user_id: user.id,
        tool_type: tool,
        title: cleanTitle,
        project_id,
      })
      .select("id, tool_type, title, project_id, created_at, updated_at")
      .single();
    if (error) throw error;
    const conv = { ...data, tool: data.tool_type };
    conversationSettingsStore.save(conv.id, defaultSettings(tool));
    return conv;
  }

  const conv = {
    id: uid("c"),
    tool,
    tool_type: tool,
    title: cleanTitle,
    project_id,
    created_at: nowIso(),
    updated_at: nowIso(),
  };
  lsSave(LS_CONVERSATIONS, [conv, ...lsLoad(LS_CONVERSATIONS)]);
  conversationSettingsStore.save(conv.id, defaultSettings(tool));
  return conv;
}

export async function renameConversation(user, id, title) {
  const clean = (title || "").trim();
  if (!clean) throw new Error("Title cannot be empty");
  if (user) {
    const { data, error } = await supabase
      .from("conversations")
      .update({ title: clean, updated_at: nowIso() })
      .eq("id", id)
      .select("id, tool_type, title, project_id, created_at, updated_at")
      .single();
    if (error) throw error;
    return { ...data, tool: data.tool_type };
  }
  const all = lsLoad(LS_CONVERSATIONS);
  const idx = all.findIndex((c) => c.id === id);
  if (idx === -1) return null;
  all[idx] = { ...all[idx], title: clean, updated_at: nowIso() };
  lsSave(LS_CONVERSATIONS, all);
  return all[idx];
}

export async function moveConversation(user, id, project_id) {
  if (user) {
    const { data, error } = await supabase
      .from("conversations")
      .update({ project_id: project_id || null, updated_at: nowIso() })
      .eq("id", id)
      .select("id, tool_type, title, project_id, created_at, updated_at")
      .single();
    if (error) throw error;
    return { ...data, tool: data.tool_type };
  }
  const all = lsLoad(LS_CONVERSATIONS);
  const idx = all.findIndex((c) => c.id === id);
  if (idx === -1) return null;
  all[idx] = { ...all[idx], project_id: project_id || null, updated_at: nowIso() };
  lsSave(LS_CONVERSATIONS, all);
  return all[idx];
}

export async function touchConversation(user, id, patch = {}) {
  if (user) {
    const { error } = await supabase
      .from("conversations")
      .update({ ...patch, updated_at: nowIso() })
      .eq("id", id);
    if (error) console.warn("touchConversation supabase error:", error.message);
    return;
  }
  const all = lsLoad(LS_CONVERSATIONS);
  const idx = all.findIndex((c) => c.id === id);
  if (idx === -1) return;
  all[idx] = { ...all[idx], ...patch, updated_at: nowIso() };
  lsSave(LS_CONVERSATIONS, all);
}

export async function deleteConversation(user, id) {
  if (user) {
    const { error } = await supabase.from("conversations").delete().eq("id", id);
    if (error) throw error;
  } else {
    lsSave(LS_CONVERSATIONS, lsLoad(LS_CONVERSATIONS).filter((c) => c.id !== id));
  }
  messagesStore.remove(id);
}

// Auto-title from first user message if the conversation still has the
// default placeholder title. Returns the new title (or null if unchanged).
export function suggestTitleFromMessage(currentTitle, firstUserContent) {
  if (!firstUserContent) return null;
  const looksLikePlaceholder = /·\s*(Conversation\s*#\d+|New chat)$/i.test(currentTitle || "");
  if (currentTitle && !looksLikePlaceholder) return null;
  const snippet = firstUserContent.slice(0, 56).trim();
  if (!snippet) return null;
  return snippet + (firstUserContent.length > 56 ? "…" : "");
}
